import { FC } from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { InternalOverviewResponse } from '../../types/internal';
import { InternalKPICard, InternalChartCard, InternalSectionHeader } from './index';
import { formatNumber, tooltipStyle } from '../../utils/formatters';

interface InternalAutomationSectionProps {
    automation: InternalOverviewResponse['automation'];
    periodLabel: string;
    periodLabelFull: string;
}

export const InternalAutomationSection: FC<InternalAutomationSectionProps> = ({ automation, periodLabel, periodLabelFull }) => {
    if (!automation) return null;

    const {
        stats = { postsGenerated: 0, companiesScraped: 0, articlesProcessed: 0, topicsGenerated: 0 },
        growth = [],
        byType = [],
        avgDuration = 0
    } = automation;

    // Format growth data for the chart
    const growthData = growth.map(d => ({
        date: d.date ? format(new Date(d.date), 'MMM d') : 'N/A',
        posts: d.posts || 0,
        scraped: d.scraped || 0,
        articles: d.articles || 0,
    }));

    return (
        <div className="space-y-4">
            <section>
                <InternalSectionHeader title="System Automation & Growth" />
                <div className="grid grid-cols-5 gap-3">
                    <InternalKPICard
                        label="Social Posts"
                        value={formatNumber(stats.postsGenerated ?? 0)}
                        sublabelText="AI Generated"
                        tooltip="Total social media posts generated by the AI system."
                    />
                    <InternalKPICard
                        label="Companies Researched"
                        value={formatNumber(stats.companiesScraped ?? 0)}
                        sublabelText="LinkedIn Scans"
                        tooltip="Number of companies identified and analyzed by the intelligence engine."
                    />
                    <InternalKPICard
                        label="Articles Processed"
                        value={formatNumber(stats.articlesProcessed ?? 0)}
                        sublabelText="News Feed"
                        tooltip="Total RSS news articles ingested and processed."
                    />
                    <InternalKPICard
                        label="Topics Identified"
                        value={formatNumber(stats.topicsGenerated ?? 0)}
                        sublabelText="Content Insights"
                        tooltip="Unique topics and trends discovered from the news feed."
                    />
                    <InternalKPICard
                        label="Total Runs"
                        value={formatNumber(automation.inRange ?? 0)}
                        sublabelText={periodLabel}
                        primary
                        tooltip="Total system executions in the selected period."
                    />
                </div>
            </section>

            <section className="grid grid-cols-3 gap-3">
                {/* Growth Chart */}
                <InternalChartCard
                    title="Platform Activity Trend"
                    subtitle={`Automation growth, ${periodLabelFull}`}
                    className="col-span-2"
                    description="Daily trend of social posts, company research, and article processing."
                >
                    <div className="h-56 pt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={growthData} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#262626" stopOpacity={0.1} />
                                        <stop offset="95%" stopColor="#262626" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="colorScraped" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#737373" stopOpacity={0.1} />
                                        <stop offset="95%" stopColor="#737373" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#f5f5f5" strokeDasharray="3 3" vertical={false} />
                                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 9, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={tooltipStyle} />
                                <Area type="monotone" dataKey="posts" stroke="#171717" strokeWidth={2} fillOpacity={1} fill="url(#colorPosts)" name="Social Posts Generated" />
                                <Area type="monotone" dataKey="scraped" stroke="#737373" strokeWidth={2} fillOpacity={1} fill="url(#colorScraped)" name="Companies Researched" />
                                <Area type="monotone" dataKey="articles" stroke="#a3a3a3" strokeDasharray="3 3" strokeWidth={1} fill="transparent" name="Articles Processed" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="flex gap-6 mt-4 px-2">
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-sm bg-neutral-900 shadow-sm" />
                            <span className="text-[10px] font-bold text-neutral-600 uppercase tracking-tight">Social Posts</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-sm bg-neutral-500 shadow-sm" />
                            <span className="text-[10px] font-bold text-neutral-600 uppercase tracking-tight">Companies Researched</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-sm border-t-2 border-dashed border-neutral-400" />
                            <span className="text-[10px] font-bold text-neutral-600 uppercase tracking-tight">Articles Processed</span>
                        </div>
                    </div>
                </InternalChartCard>

                {/* Health Metric */}
                <InternalChartCard
                    title="System Health"
                    subtitle="Reliability Score"
                    description="Success rate of scheduled workers."
                >
                    <div className="flex flex-col items-center justify-center py-4">
                        {(() => {
                            const success = automation.successInRange || 0;
                            const failed = automation.failedInRange || 0;
                            const total = success + failed;
                            const rate = total > 0 ? (success / total) * 100 : 100;
                            const displayRate = Math.round(rate);

                            return (
                                <>
                                    <div className="relative w-20 h-20 flex items-center justify-center">
                                        <svg className="w-full h-full transform -rotate-90">
                                            <circle cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="6" fill="transparent" className="text-neutral-100" />
                                            <circle
                                                cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="6" fill="transparent"
                                                strokeDasharray={213.6}
                                                strokeDashoffset={213.6 * (1 - (rate / 100))}
                                                className={`${rate > 95 ? 'text-emerald-500' : rate > 80 ? 'text-amber-500' : 'text-rose-500'} transition-all duration-1000`}
                                            />
                                        </svg>
                                        <span className="absolute text-lg font-bold text-neutral-900">{displayRate}%</span>
                                    </div>
                                    <div className="mt-6 w-full space-y-2.5 px-2">
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-neutral-500 font-medium">Successful Runs</span>
                                            <span className="text-neutral-900 font-bold">{formatNumber(success)}</span>
                                        </div>
                                        <div className="flex justify-between text-[11px]">
                                            <span className="text-neutral-500 font-medium">Failed Runs</span>
                                            <span className="text-rose-600 font-bold">{formatNumber(failed)}</span>
                                        </div>
                                        <div className="pt-2 border-t border-neutral-100 flex justify-between text-[11px]">
                                            <span className="text-neutral-500 font-medium">Avg Latency</span>
                                            <span className="text-neutral-900 font-bold">{Math.round(avgDuration)}s</span>
                                        </div>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </InternalChartCard>

                {/* Automation Types Breakdown */}
                <InternalChartCard
                    title="Workload Distribution"
                    subtitle="Resource allocation"
                    description="Breakdown of system resources by module."
                    className="col-span-3"
                >
                    <div className="grid grid-cols-4 gap-4 pt-2">
                        {(byType || []).slice(0, 4).map((t, i) => {
                            const label = t.type?.includes('MXM') ? 'Generation' :
                                t.type?.includes('LINKEDIN') ? 'Discovery' :
                                    t.type?.includes('RSS') ? 'Content Ingest' : (t.type || 'System');
                            return (
                                <div key={i} className="flex flex-col border-r border-neutral-50 last:border-0 pr-4">
                                    <div className="text-[10px] font-bold text-neutral-500 uppercase tracking-tight mb-1 truncate">{label}</div>
                                    <div className="flex items-baseline gap-1.5">
                                        <span className="text-lg font-black text-neutral-900 tracking-tighter">{formatNumber(t.count)}</span>
                                        <span className="text-[9px] text-neutral-400 font-medium uppercase">runs</span>
                                    </div>
                                    <div className="mt-2 h-1 w-full bg-neutral-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-neutral-800"
                                            style={{ width: `${Math.min(100, (t.count / (automation.inRange || 1)) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </InternalChartCard>
            </section>
        </div>
    );
};
